import { useNavigate } from 'react-router-dom';
import { ArrowRight, Package, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useServices } from '@/lib/queries';
import { serviceIcon } from '@/lib/serviceIcons';
import { PageHeader } from '@/components/PageHeader';
import { LocaleChips } from './Articles';
import { Button } from '@/components/ui/Button';
import { EmptyState, LiveDot, Skeleton } from '@/components/ui/Feedback';

/**
 * The list lives in its own module, separate from ServiceEditor.
 *
 * That is a bundle decision, not a style one: the editor pulls TipTap and
 * Framer Motion, and a shared module would drag both into the first paint even
 * though the editor route is lazy (§10.9).
 */

// A curated, harmonious accent set for the icon tiles. Services are the one
// place in the app with a per-item glyph, so colour is spent here — each card
// gets a stable hue by position, turning the list into a legible icon system
// (the signature that sets it apart from the photo-led news cards). Every hue
// is a ~600-weight jewel tone that stays readable on a soft tint in light and
// dark alike; everything else on the card stays neutral.
const ACCENTS = ['#2f4dec', '#0d9488', '#7c3aed', '#d97706', '#db2777', '#0284c7'];

export function Services() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useServices();
  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t.services.title}
        subtitle={t.services.subtitle}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/services/new')}>
            {t.services.new}
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        {isLoading ? (
          <CardGridSkeleton />
        ) : rows.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<Package className="h-6 w-6" />}
              title={t.services.emptyTitle}
              hint={t.services.emptyHint}
              action={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/services/new')}>
                  {t.services.emptyAction}
                </Button>
              }
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((service, i) => {
              const Icon = serviceIcon(service.icon);
              const accent = ACCENTS[i % ACCENTS.length];
              const title = service.title[lang] ?? service.title.uz ?? t.articles.untitled;
              const hasTitle = Boolean(service.title[lang] ?? service.title.uz);
              const excerpt = service.excerpt?.[lang] ?? service.excerpt?.uz ?? null;
              const cover = service.cover?.url ?? null;
              const live = service.status === 'PUBLISHED';

              return (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => navigate(`/services/${service.id}`)}
                  className="card card-hover group flex flex-col overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                >
                  {/* One clear visual per service: its cover photo, or — when
                      there's none — the service's own icon on its accent colour.
                      Same top-image shape as the news cards, so staff learn one
                      pattern. No overlays, no medallion. */}
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-quick ease-out group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div
                        className="absolute inset-0 grid place-items-center"
                        style={{ background: `linear-gradient(135deg, ${accent}, ${accent}c8)` }}
                      >
                        <Icon aria-hidden className="h-12 w-12 text-white/95" />
                      </div>
                    )}

                    <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-chip bg-surface/90 px-2 py-0.5 text-eyebrow uppercase text-muted shadow-card backdrop-blur">
                      <LiveDot live={live} />
                      {live ? t.status.live : t.status.draft}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col p-4">
                    {/* A small icon on photo cards keeps the service's identity
                        visible; photo-less cards already show it in the banner. */}
                    <div className="flex items-start gap-2">
                      {cover && (
                        <span
                          className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md"
                          style={{ backgroundColor: `${accent}1f`, color: accent }}
                        >
                          <Icon aria-hidden className="h-3.5 w-3.5" />
                        </span>
                      )}
                      <h3 className={cn('line-clamp-2 text-[0.9375rem] font-semibold leading-snug', hasTitle ? 'text-strong' : 'text-muted')}>
                        {title}
                      </h3>
                    </div>

                    {excerpt ? (
                      <p className="mt-1.5 line-clamp-2 text-ui leading-relaxed text-muted">{excerpt}</p>
                    ) : (
                      <p className="mt-1 truncate font-mono text-code text-faint">/{service.slug}</p>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                      <LocaleChips completeness={service.completeness} />
                      <span className="inline-flex shrink-0 items-center gap-1 text-ui font-medium text-brand-600 transition-colors group-hover:text-brand-500 dark:text-brand-300">
                        {t.common.details}
                        <ArrowRight aria-hidden className="h-3.5 w-3.5 transition-transform duration-quick ease-out group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/** Placeholder cards while services load — matches the real image-top shape. */
function CardGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="card overflow-hidden">
          <Skeleton className="aspect-[16/9] w-full rounded-none" />
          <div className="space-y-2.5 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <div className="mt-2 flex items-center justify-between">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
